/**
 * AI Insights Card - Shows AI-powered progress insights for athletes
 *
 * Displays personalized training insights, focus areas, and trends
 * based on the athlete's video history and coach feedback.
 */

import { useState, useEffect } from 'react';
import { getProgressInsights, type ProgressInsights } from '@tss/shared';
import { Sparkles, TrendingUp, Target, ChevronRight, RefreshCw, Loader2 } from 'lucide-react';
import { Badge } from './UI';

interface AIInsightsCardProps {
  onViewFullProgress?: () => void;
}

export function AIInsightsCard({ onViewFullProgress }: AIInsightsCardProps) {
  const [insights, setInsights] = useState<ProgressInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadInsights();
  }, []);

  async function loadInsights() {
    try {
      setError(null);
      const data = await getProgressInsights();
      setInsights(data);
    } catch (err) {
      console.error('Failed to load AI insights:', err);
      setError('Unable to load insights');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await loadInsights();
  }

  if (loading) {
    return (
      <div
        className="card"
        style={{
          padding: '24px',
          background: 'linear-gradient(135deg, rgba(123, 75, 216, 0.06) 0%, rgba(168, 85, 247, 0.02) 100%)',
          border: '1px solid rgba(123, 75, 216, 0.15)',
        }}
      >
        <div className="flex-center" style={{ padding: '20px' }}>
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent)' }} />
        </div>
      </div>
    );
  }

  if (error || !insights) {
    return null; // Silently fail - don't show error card
  }

  // Don't show if there are no meaningful insights
  if (!insights.insights || insights.insights.includes('Upload more training videos')) {
    return null;
  }

  return (
    <div
      className="card"
      style={{
        padding: '20px',
        background: 'linear-gradient(135deg, rgba(123, 75, 216, 0.06) 0%, rgba(168, 85, 247, 0.02) 100%)',
        border: '1px solid rgba(123, 75, 216, 0.15)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'rgba(123, 75, 216, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sparkles size={18} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: 'var(--gray-900)', margin: 0 }}>
              AI Coach Insights
            </h3>
            <p style={{ fontSize: '11px', color: 'var(--gray-500)', margin: 0 }}>
              Personalized for you
            </p>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="btn-ghost"
          style={{
            padding: '6px',
            borderRadius: '50%',
            opacity: refreshing ? 0.5 : 1,
          }}
          title="Refresh insights"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} style={{ color: 'var(--gray-400)' }} />
        </button>
      </div>

      {/* Main Insight */}
      <p style={{ fontSize: '14px', color: 'var(--gray-700)', lineHeight: '1.6', marginBottom: '16px' }}>
        {insights.insights}
      </p>

      {/* Focus Areas */}
      {insights.focus_areas && insights.focus_areas.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <Target size={14} style={{ color: 'var(--warning)' }} />
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--gray-600)' }}>
              Focus Areas
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {insights.focus_areas.slice(0, 3).map((area, i) => (
              <Badge key={i} variant="warning" size="sm" pill uppercase={false}>
                {area}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Positive Trends */}
      {insights.positive_trends && insights.positive_trends.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <TrendingUp size={14} style={{ color: 'var(--success)' }} />
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--gray-600)' }}>
              Positive Trends
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {insights.positive_trends.slice(0, 3).map((trend, i) => (
              <Badge key={i} variant="success" size="sm" pill uppercase={false}>
                {trend}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Activity Trends */}
      {insights.trends && insights.trends.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '12px',
            padding: '12px',
            background: 'rgba(123, 75, 216, 0.04)',
            borderRadius: 'var(--radius-md)',
            marginBottom: '12px',
          }}
        >
          {insights.trends.slice(0, 3).map((trend, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
              <p style={{ fontSize: '18px', fontWeight: '700', color: 'var(--accent)' }}>
                {trend.count}
              </p>
              <p style={{ fontSize: '11px', color: 'var(--gray-500)', textTransform: 'capitalize' }}>
                {trend.activity.replace(/_/g, ' ')}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* View Progress Link */}
      {onViewFullProgress && (
        <button
          onClick={onViewFullProgress}
          className="btn-ghost"
          style={{
            width: '100%',
            padding: '10px',
            color: 'var(--accent)',
            fontSize: '13px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
          }}
        >
          View Full Progress
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
}
